import {
	Component,
	signal,
	WritableSignal,
	input,
	output,
	Signal,
	computed,
	OnDestroy,
	effect,
	viewChild,
	inject
} from "@angular/core";
import { Platform } from "@ionic/angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DateTime } from "luxon";
import { LuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS } from "@angular/material-luxon-adapter";
import { IEvent, IEventUser, IModule, ISchedule } from "src/app/shared/interfaces";
import * as _ from "lodash-es";
import { UtilityService } from "src/app/shared/services";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { MaterialModule } from "src/app/shared/global-modules/material-modules";
import { CommonModule } from "@angular/common";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { environment } from "src/environments/environment";

export const DATE_PICKER_FORMAT = {
	parse: {
		dateInput: "dd/MM/yyyy"
	},
	display: {
		dateInput: "dd/MM/yyyy",
		monthLabel: "",
		monthYearLabel: "MMMM yyyy",
		dateA11yLabel: "LL",
		monthYearA11yLabel: "MMMM yyyy"
	}
};

@Component({
	selector: "app-calendar",
	templateUrl: "./calendar.component.html",
	styleUrls: ["./calendar.component.scss"],
	standalone: true,
	imports: [CommonModule, FormsModule, MaterialModule, ReactiveFormsModule, TranslateModule],
	providers: [
		{
			provide: DateAdapter,
			useClass: LuxonDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS]
		},
		{ provide: MAT_DATE_FORMATS, useValue: DATE_PICKER_FORMAT }
	]
})
export class CalendarComponent implements OnDestroy {
	private readonly _adapter = inject<DateAdapter<unknown, unknown>>(DateAdapter);
	STranslate = inject(TranslateService);
	datePicker = viewChild("picker");
	destroyComponent = new Subject();
	event = input<IEvent>(null);
	module = input<IModule>(null);
	eventUser = input<IEventUser>(null);
	sessions = input([], {
		transform: (values: ISchedule[]): ISchedule[] => {
			return values.sort((a, b) =>
				DateTime.fromISO(a.startDate).diff(DateTime.fromISO(b.startDate), "milliseconds").milliseconds >= 0
					? 1
					: -1
			);
		}
	});

	nextDateWithSessions: Signal<DateTime> = computed(() => {
		const sessions = this.sessions();
		const currentDate = DateTime.local();
		let firstDateOfCalendar: DateTime;
		sessions.forEach((session) => {
			const diff = DateTime.fromISO(session.startDate).diff(currentDate, "milliseconds").milliseconds;
			if (diff >= 0 && !firstDateOfCalendar) {
				firstDateOfCalendar = DateTime.fromISO(session.startDate);
			}
		});
		return firstDateOfCalendar ? firstDateOfCalendar.startOf("day") : DateTime.local();
	});

	baseDaysOnCalendar: Signal<DateTime[]> = computed(() => {
		const baseDate = this.nextDateWithSessions();
		const days = [baseDate];
		// const isMobile = this.isMobile();

		for (let i = 0; i < 6; i++) {
			days.push(baseDate.plus({ day: i + 1 }));
		}
		return days;
	});

	pickerDateFormControl: WritableSignal<FormControl> = signal(new FormControl());
	minDatePicker: Signal<DateTime> = computed(() => {
		return DateTime.fromISO(this.event().startDate).startOf("day");
	});
	maxDatePicker: Signal<DateTime> = computed(() => {
		return DateTime.fromISO(this.event().endDate).endOf("day");
	});

	showFullCalendar: WritableSignal<boolean> = signal(false);

	selectedDate: WritableSignal<DateTime> = signal(null);

	selectedDateChange = output<DateTime>();

	isMobile: WritableSignal<boolean> = signal(false);

	currentLanguage: WritableSignal<string> = signal(environment.platform.defaultLanguage);

	firstTime: boolean = true;

	constructor(
		public SUtility: UtilityService,
		private platform: Platform
	) {
		this.currentLanguage.set(this.STranslate.currentLang);
		this.setAdapterLocale();
		this.STranslate.onLangChange.pipe(takeUntil(this.destroyComponent)).subscribe((lang) => {
			this.currentLanguage.set(lang.lang);
			this.setAdapterLocale();
		});
		this.isMobile.set(
			this.platform.is("mobile") || this.platform.is("mobileweb") || window.innerWidth < 768 ? true : false
		);

		effect(() => {
			const selectedDate = this.selectedDate();
			if (selectedDate || (!this.firstTime && !selectedDate)) {
				this.firstTime = false;
				this.selectedDateChange.emit(selectedDate);
			}
		});

		this.pickerDateFormControl().valueChanges.subscribe((changes) => {
			const formDate = this.pickerDateFormControl().getRawValue();
			if (
				this.selectedDate() &&
				formDate &&
				this.selectedDate().hasSame(formDate, "year") &&
				this.selectedDate().hasSame(formDate, "month") &&
				this.selectedDate().hasSame(formDate, "day")
			) {
				this.pickerDateFormControl().patchValue(null);
			} else {
				this.selectedDate.set(formDate);
			}
		});
	}

	ngOnDestroy() {
		this.destroyComponent.complete();
	}

	checkSessionsInDay = (day: DateTime) => {
		const sessions = this.sessions();
		let check: boolean = false;

		for (let i = 0; i < sessions.length; i++) {
			const session = sessions[i];
			const sessionDay = DateTime.fromISO(session.startDate);
			if (
				sessionDay &&
				day &&
				sessionDay.hasSame(day, "year") &&
				sessionDay.hasSame(day, "month") &&
				sessionDay.hasSame(day, "day")
			) {
				check = true;
				break;
			}
		}
		return check;
	};

	checkIsSelectedDate(day: DateTime) {
		return (
			this.selectedDate() &&
			day &&
			this.selectedDate().hasSame(day, "year") &&
			this.selectedDate().hasSame(day, "month") &&
			this.selectedDate().hasSame(day, "day")
		);
	}

	dateSelected(day: DateTime) {
		if (
			!day ||
			(this.selectedDate() &&
				day &&
				this.selectedDate().hasSame(day, "year") &&
				this.selectedDate().hasSame(day, "month") &&
				this.selectedDate().hasSame(day, "day"))
		) {
			this.pickerDateFormControl().patchValue(null);
			this.selectedDate.set(null);
		} else {
			this.selectedDate.set(day);
		}
	}

	setAdapterLocale() {
		this._adapter.setLocale(
			this.currentLanguage().slice(0, 2).toLowerCase() + "-" + this.currentLanguage().slice(2, 4).toUpperCase()
		);
	}

	// @Input() event: IEvent;
	// @Input() eventUser: IEventUser;
	// @Input() selectedDate: string = "";
	// @Input() datasForBadges: { date: string; totalDatas: number }[] = [];
	// @Output() dateSelectedEvent: EventEmitter<{ startDate: string; endDate: string }> = new EventEmitter();

	// @Input() calendarClosed: boolean = false;
	// @Input() viewType: "month" | "week" = "week";

	// @Input() selectedDateTime: DateTime;
	// currentDateTime: WritableSignal<DateTime> = signal(null);
	// currentWeekDateTime: WritableSignal<DateTime> = signal(null);
	// savedCurrentDateTime: DateTime;

	// @Input() currentVisibleDateOnList: string = "";

	// selectedStartDate: IDay;
	// selectedEndDate: IDay;

	// stringMonths: string[] = [];
	// stringDays: string[] = [];

	// dates: IYear[] = [];

	// isMobile: boolean = false;

	// timeStartModalOpen: boolean = false;
	// timeStart: string = "";
	// timeEndModalOpen: boolean = false;
	// timeEnd: string = "";

	// ngOnInit() {
	// 	this.isMobile =
	// 		this.platform.is("mobile") || this.platform.is("mobileweb") || window.innerWidth < 768 ? true : false;
	// 	// this.viewType = this.viewType ? this.viewType : this.isMobile ? "week" : "month";

	// 	if (this.selectedDate) {
	// 		this.selectedDateTime = DateTime.fromISO(this.selectedDate).setLocale(
	// 			this.STranslate.currentLang
	// 				? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.STranslate.currentLang.slice(2, 4).toUpperCase()
	// 				: this.event.language.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.event.language.slice(2, 4).toUpperCase()
	// 		);

	// 		if (
	// 			this.event &&
	// 			this.event.timezoneType === "local" &&
	// 			this.eventUser &&
	// 			this.eventUser.updatedSettings &&
	// 			this.eventUser.updatedSettings.timezoneType === "event"
	// 		) {
	// 			this.selectedDateTime = this.selectedDateTime.setZone(this.event.timezone);
	// 		}
	// 	}

	// 	this.currentDateTime.set(
	// 		DateTime.local().setLocale(
	// 			this.STranslate.currentLang
	// 				? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.STranslate.currentLang.slice(2, 4).toUpperCase()
	// 				: this.event.language.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.event.language.slice(2, 4).toUpperCase()
	// 		)
	// 	);

	// 	if (
	// 		this.event &&
	// 		this.event.timezoneType === "local" &&
	// 		this.eventUser &&
	// 		this.eventUser.updatedSettings &&
	// 		this.eventUser.updatedSettings.timezoneType === "event"
	// 	) {
	// 		this.currentDateTime.set(this.currentDateTime().setZone(this.event.timezone));
	// 	}
	// 	this.currentWeekDateTime.set(this.currentDateTime());

	// 	this.savedCurrentDateTime = _.cloneDeep(this.currentDateTime());

	// 	this.stringMonths = Info.months(this.isMobile ? "short" : "long", {
	// 		locale: this.STranslate.currentLang
	// 			? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
	// 			  "-" +
	// 			  this.STranslate.currentLang.slice(2, 4).toUpperCase()
	// 			: this.event.language.slice(0, 2).toLowerCase() + "-" + this.event.language.slice(2, 4).toUpperCase()
	// 	});
	// 	this.stringDays = Info.weekdays(this.isMobile ? "short" : "long", {
	// 		locale: this.STranslate.currentLang
	// 			? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
	// 			  "-" +
	// 			  this.STranslate.currentLang.slice(2, 4).toUpperCase()
	// 			: this.event.language.slice(0, 2).toLowerCase() + "-" + this.event.language.slice(2, 4).toUpperCase()
	// 	});

	// 	this.generateDatesOfCurrentDate();
	// }

	// ngOnChanges(changes: SimpleChanges) {
	// 	if (changes && changes.datasForBadges && !changes.datasForBadges.firstChange) {
	// 		this.generateDatesOfCurrentDate();
	// 	}
	// }

	// generateDatesOfCurrentDate() {
	// 	const year = this.currentDateTime().year;
	// 	const month = this.currentDateTime().month;
	// 	const beginWeekOfMonth = this.currentDateTime().startOf("month").weekNumber;
	// 	const endWeekOfMonth =
	// 		this.currentDateTime().endOf("month").weekNumber > this.currentDateTime().startOf("month").weekNumber
	// 			? this.currentDateTime().endOf("month").weekNumber
	// 			: this.currentDateTime().endOf("month").weeksInWeekYear;

	// 	const nextYearOverlap: boolean = this.currentDateTime().endOf("month").weekNumber === 1;

	// 	const isExistingYear = this.checkYearOnDates(year);
	// 	const yearData = isExistingYear
	// 		? isExistingYear
	// 		: {
	// 				yearNumber: year,
	// 				months: {
	// 					1: { monthNumber: 1, weeks: [] },
	// 					2: { monthNumber: 2, weeks: [] },
	// 					3: { monthNumber: 3, weeks: [] },
	// 					4: { monthNumber: 4, weeks: [] },
	// 					5: { monthNumber: 5, weeks: [] },
	// 					6: { monthNumber: 6, weeks: [] },
	// 					7: { monthNumber: 7, weeks: [] },
	// 					8: { monthNumber: 8, weeks: [] },
	// 					9: { monthNumber: 9, weeks: [] },
	// 					10: { monthNumber: 10, weeks: [] },
	// 					11: { monthNumber: 11, weeks: [] },
	// 					12: { monthNumber: 12, weeks: [] }
	// 				}
	// 		  };

	// 	const isExistingMonth = this.checkMonthOnYear(yearData, month as any);
	// 	const monthData = isExistingMonth
	// 		? isExistingMonth
	// 		: {
	// 				monthNumber: month,
	// 				weeks: []
	// 		  };
	// 	// if (isExistingYear && isExistingMonth && isExistingMonth.weeks && isExistingMonth.weeks.length > 0) {
	// 	// 	return;
	// 	// }
	// 	monthData.weeks = [];
	// 	for (let i = beginWeekOfMonth; i <= endWeekOfMonth; i++) {
	// 		monthData.weeks.push(this.generateDayDatas(i, this.currentDateTime()));
	// 	}

	// 	if (nextYearOverlap) {
	// 		monthData.weeks.push(
	// 			this.generateDayDatas(
	// 				1,
	// 				DateTime.fromObject({
	// 					weekNumber: 1,
	// 					weekYear: year + 1
	// 				}).startOf("week")
	// 			)
	// 		);
	// 	}

	// 	yearData.months[month] = monthData;

	// 	if (!this.dates.find((year) => year.yearNumber === yearData.yearNumber)) {
	// 		this.dates.push(yearData);
	// 	}
	// }

	// generateDayDatas(i: number, currentDateTime: DateTime) {
	// 	const daysOfWeek = [];
	// 	const dayOfWeek = DateTime.fromObject({
	// 		weekNumber: i,
	// 		weekYear: currentDateTime.weekYear
	// 	}).startOf("week");
	// 	const weekNumber = dayOfWeek.weekNumber;
	// 	let day = dayOfWeek.startOf("day");
	// 	for (let iDay = 0; iDay < 7; iDay++) {
	// 		if (iDay === 0) {
	// 			const date = this.getDateOfDatasForBadge(day.toISO());
	// 			// First day of week
	// 			daysOfWeek.push({
	// 				isoDate: day.toISO(),
	// 				isInCurrentMonth:
	// 					(this.selectedDate && this.selectedDateTime && this.selectedDateTime.month === day.month) ||
	// 					(!this.selectedDate && !this.selectedDateTime && currentDateTime.month === day.month)
	// 						? true
	// 						: false,
	// 				dayNumber: day.day,
	// 				weekNumber: day.weekNumber,
	// 				monthNumber: day.month,
	// 				yearNumber: day.year,
	// 				isSelectedDate: this.checkIfIsSelectedDate(day),
	// 				isCurrentDate: this.checkIfIsCurrentDate(day),
	// 				totalDatas: date ? date.totalDatas : 0
	// 			});
	// 		} else {
	// 			// Other days of week
	// 			day = day.plus({ day: 1 });
	// 			const date = this.getDateOfDatasForBadge(day.toISO());
	// 			daysOfWeek.push({
	// 				isoDate: day.toISO(),
	// 				isInCurrentMonth:
	// 					(this.selectedDate && this.selectedDateTime && this.selectedDateTime.month === day.month) ||
	// 					(!this.selectedDate && !this.selectedDateTime && currentDateTime.month === day.month)
	// 						? true
	// 						: false,
	// 				dayNumber: day.day,
	// 				weekNumber: day.weekNumber,
	// 				monthNumber: day.month,
	// 				yearNumber: day.year,
	// 				isSelectedDate: this.checkIfIsSelectedDate(day),
	// 				isCurrentDate: this.checkIfIsCurrentDate(day),
	// 				totalDatas: date ? date.totalDatas : 0
	// 			});
	// 		}
	// 	}

	// 	return {
	// 		weekNumber: weekNumber,
	// 		days: daysOfWeek
	// 	};
	// }

	// checkIfIsSelectedDate(date: DateTime) {
	// 	if (
	// 		this.selectedDate &&
	// 		this.selectedDateTime &&
	// 		this.selectedDateTime.year === date.year &&
	// 		this.selectedDateTime.month === date.month &&
	// 		this.selectedDateTime.day === date.day
	// 	) {
	// 		return true;
	// 	} else {
	// 		return false;
	// 	}
	// }

	// checkIfIsCurrentDate(date: DateTime) {
	// 	if (
	// 		this.currentDateTime() &&
	// 		this.currentDateTime().year === date.year &&
	// 		this.currentDateTime().month === date.month &&
	// 		this.currentDateTime().day === date.day
	// 	) {
	// 		return true;
	// 	} else {
	// 		return false;
	// 	}
	// }

	// getCurrentMonth() {
	// 	if (this.selectedDate) {
	// 		let date = DateTime.fromISO(this.selectedDate).setLocale(
	// 			this.STranslate.currentLang
	// 				? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.STranslate.currentLang.slice(2, 4).toUpperCase()
	// 				: this.event.language.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.event.language.slice(2, 4).toUpperCase()
	// 		);

	// 		if (
	// 			this.event &&
	// 			this.event.timezoneType === "local" &&
	// 			this.eventUser &&
	// 			this.eventUser.updatedSettings &&
	// 			this.eventUser.updatedSettings.timezoneType === "event"
	// 		) {
	// 			date = date.setZone(this.event.timezone);
	// 		}
	// 		return date.toFormat("MMMM");
	// 	} else {
	// 		return this.currentDateTime().toFormat("MMMM");
	// 	}
	// }

	// getCurrentYear() {
	// 	if (this.selectedDate) {
	// 		let date = DateTime.fromISO(this.selectedDate).setLocale(
	// 			this.STranslate.currentLang
	// 				? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.STranslate.currentLang.slice(2, 4).toUpperCase()
	// 				: this.event.language.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.event.language.slice(2, 4).toUpperCase()
	// 		);

	// 		if (
	// 			this.event &&
	// 			this.event.timezoneType === "local" &&
	// 			this.eventUser &&
	// 			this.eventUser.updatedSettings &&
	// 			this.eventUser.updatedSettings.timezoneType === "event"
	// 		) {
	// 			date = date.setZone(this.event.timezone);
	// 		}
	// 		return date.toFormat("yyyy");
	// 	} else {
	// 		return this.currentDateTime().toFormat("yyyy");
	// 	}
	// }

	// getWeeksOnDates(monthNumber: number, yearNumber: number) {
	// 	const year = this.checkYearOnDates(yearNumber);
	// 	const month = this.checkMonthOnYear(year, monthNumber as any);
	// 	return year && month && month.weeks && month.weeks.length > 0 ? month.weeks : [];
	// }

	// getWeeksOnDatesByWeek() {
	// 	const year = this.checkYearOnDates(this.currentWeekDateTime().year);
	// 	const month = this.checkMonthOnYear(year, this.currentWeekDateTime().month as any);
	// 	const week = this.checkWeekOfMonth(month, this.currentWeekDateTime().weekNumber);
	// 	return year && month && month.weeks && month.weeks.length > 0 && week && week.days.length > 0 ? week.days : [];
	// }

	// checkSameVisibleDate(day: IDay) {
	// 	const dateTimeDay = DateTime.fromISO(day.isoDate);
	// 	const dateTimeCurrentVisible = DateTime.fromISO(this.currentVisibleDateOnList);
	// 	return (
	// 		dateTimeCurrentVisible.hasSame(dateTimeDay, "year") &&
	// 		dateTimeCurrentVisible.hasSame(dateTimeDay, "month") &&
	// 		dateTimeCurrentVisible.hasSame(dateTimeDay, "day")
	// 	);
	// }

	// getSelectedStartDate() {
	// 	return this.selectedStartDate ? DateTime.fromISO(this.selectedStartDate.isoDate).toFormat("dd-MM-yyyy") : null;
	// }

	// getSelectedEndDate() {
	// 	return this.selectedEndDate ? DateTime.fromISO(this.selectedEndDate.isoDate).toFormat("dd-MM-yyyy") : null;
	// }

	// getSelectedDateTime(date: string) {
	// 	return DateTime.fromISO(date).toFormat("HH:mm");
	// }

	// selectedDateOnCalendar(day: IDay | "all" | "current") {
	// 	if (day === "all") {
	// 		this.selectedStartDate = null;
	// 		this.selectedEndDate = null;
	// 		this.dateSelectedEvent.emit({
	// 			startDate: "all",
	// 			endDate: "all"
	// 		});
	// 	} else if (day === "current") {
	// 		let dayDate = DateTime.local().setLocale(
	// 			this.STranslate.currentLang
	// 				? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.STranslate.currentLang.slice(2, 4).toUpperCase()
	// 				: this.event.language.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.event.language.slice(2, 4).toUpperCase()
	// 		);

	// 		if (
	// 			this.event &&
	// 			this.event.timezoneType === "local" &&
	// 			this.eventUser &&
	// 			this.eventUser.updatedSettings &&
	// 			this.eventUser.updatedSettings.timezoneType === "event"
	// 		) {
	// 			dayDate = dayDate.setZone(this.event.timezone) as any;
	// 		}
	// 		const date = this.getDateOfDatasForBadge(dayDate.toISO());
	// 		this.selectedStartDate = {
	// 			isoDate: dayDate.startOf("day").toISO(),
	// 			isInCurrentMonth:
	// 				(this.selectedDate && this.selectedDateTime && this.selectedDateTime.month === dayDate.month) ||
	// 				(!this.selectedDate && !this.selectedDateTime && this.currentDateTime().month === dayDate.month)
	// 					? true
	// 					: false,
	// 			dayNumber: dayDate.day,
	// 			weekNumber: dayDate.weekNumber,
	// 			monthNumber: dayDate.month,
	// 			yearNumber: dayDate.year,
	// 			isSelectedDate: this.checkIfIsSelectedDate(dayDate),
	// 			isCurrentDate: this.checkIfIsCurrentDate(dayDate),
	// 			totalDatas: date ? date.totalDatas : 0
	// 		};
	// 		this.currentDateTime.set(dayDate);
	// 		this.currentWeekDateTime.set(this.currentDateTime());
	// 		this.selectedEndDate = {
	// 			isoDate: dayDate.endOf("day").toISO(),
	// 			isInCurrentMonth:
	// 				(this.selectedDate && this.selectedDateTime && this.selectedDateTime.month === dayDate.month) ||
	// 				(!this.selectedDate && !this.selectedDateTime && this.currentDateTime().month === dayDate.month)
	// 					? true
	// 					: false,
	// 			dayNumber: dayDate.day,
	// 			weekNumber: dayDate.weekNumber,
	// 			monthNumber: dayDate.month,
	// 			yearNumber: dayDate.year,
	// 			isSelectedDate: this.checkIfIsSelectedDate(dayDate),
	// 			isCurrentDate: this.checkIfIsCurrentDate(dayDate),
	// 			totalDatas: date ? date.totalDatas : 0
	// 		};
	// 		this.dateSelectedEvent.emit({
	// 			startDate: dayDate.startOf("day").toISO(),
	// 			endDate: dayDate.endOf("day").toISO()
	// 		});
	// 	} else {
	// 		const dateTimeDay = DateTime.fromISO(day.isoDate);
	// 		const dateTimeSelectedStart = this.selectedStartDate
	// 			? DateTime.fromISO(this.selectedStartDate.isoDate)
	// 			: null;
	// 		const isEndDate =
	// 			this.selectedStartDate && dateTimeSelectedStart && dateTimeSelectedStart < dateTimeDay ? true : false;
	// 		if (
	// 			(this.selectedStartDate &&
	// 				day.dayNumber === this.selectedStartDate.dayNumber &&
	// 				day.monthNumber === this.selectedStartDate.monthNumber &&
	// 				day.yearNumber === this.selectedStartDate.yearNumber) ||
	// 			(this.selectedEndDate &&
	// 				day.dayNumber === this.selectedEndDate.dayNumber &&
	// 				day.monthNumber === this.selectedEndDate.monthNumber &&
	// 				day.yearNumber === this.selectedEndDate.yearNumber)
	// 		) {
	// 			if (
	// 				this.selectedStartDate &&
	// 				day.dayNumber === this.selectedStartDate.dayNumber &&
	// 				day.monthNumber === this.selectedStartDate.monthNumber &&
	// 				day.yearNumber === this.selectedStartDate.yearNumber
	// 			) {
	// 				if (
	// 					this.selectedEndDate &&
	// 					(day.dayNumber !== this.selectedEndDate.dayNumber ||
	// 						day.monthNumber !== this.selectedEndDate.monthNumber ||
	// 						day.yearNumber !== this.selectedEndDate.yearNumber)
	// 				) {
	// 					let endDate = DateTime.fromISO(this.selectedEndDate.isoDate).setLocale(
	// 						this.STranslate.currentLang
	// 							? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
	// 									"-" +
	// 									this.STranslate.currentLang.slice(2, 4).toUpperCase()
	// 							: this.event.language.slice(0, 2).toLowerCase() +
	// 									"-" +
	// 									this.event.language.slice(2, 4).toUpperCase()
	// 					);

	// 					if (
	// 						this.event &&
	// 						this.event.timezoneType === "local" &&
	// 						this.eventUser &&
	// 						this.eventUser.updatedSettings &&
	// 						this.eventUser.updatedSettings.timezoneType === "event"
	// 					) {
	// 						endDate = endDate.setZone(this.event.timezone);
	// 					}
	// 					this.selectedStartDate = { ...this.selectedEndDate, isoDate: endDate.startOf("day").toISO() };
	// 				} else {
	// 					this.selectedStartDate = null;
	// 				}
	// 			}
	// 			if (
	// 				this.selectedEndDate &&
	// 				day.dayNumber === this.selectedEndDate.dayNumber &&
	// 				day.monthNumber === this.selectedEndDate.monthNumber &&
	// 				day.yearNumber === this.selectedEndDate.yearNumber
	// 			) {
	// 				if (
	// 					this.selectedStartDate &&
	// 					(day.dayNumber !== this.selectedStartDate.dayNumber ||
	// 						day.monthNumber !== this.selectedStartDate.monthNumber ||
	// 						day.yearNumber !== this.selectedStartDate.yearNumber)
	// 				) {
	// 					let startDate = DateTime.fromISO(this.selectedStartDate.isoDate).setLocale(
	// 						this.STranslate.currentLang
	// 							? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
	// 									"-" +
	// 									this.STranslate.currentLang.slice(2, 4).toUpperCase()
	// 							: this.event.language.slice(0, 2).toLowerCase() +
	// 									"-" +
	// 									this.event.language.slice(2, 4).toUpperCase()
	// 					);

	// 					if (
	// 						this.event &&
	// 						this.event.timezoneType === "local" &&
	// 						this.eventUser &&
	// 						this.eventUser.updatedSettings &&
	// 						this.eventUser.updatedSettings.timezoneType === "event"
	// 					) {
	// 						startDate = startDate.setZone(this.event.timezone);
	// 					}
	// 					this.selectedEndDate = { ...this.selectedStartDate, isoDate: startDate.endOf("day").toISO() };
	// 				} else {
	// 					this.selectedEndDate = null;
	// 				}
	// 			}
	// 		} else if (isEndDate) {
	// 			let endDate = DateTime.fromISO(day.isoDate).setLocale(
	// 				this.STranslate.currentLang
	// 					? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
	// 							"-" +
	// 							this.STranslate.currentLang.slice(2, 4).toUpperCase()
	// 					: this.event.language.slice(0, 2).toLowerCase() +
	// 							"-" +
	// 							this.event.language.slice(2, 4).toUpperCase()
	// 			);

	// 			if (
	// 				this.event &&
	// 				this.event.timezoneType === "local" &&
	// 				this.eventUser &&
	// 				this.eventUser.updatedSettings &&
	// 				this.eventUser.updatedSettings.timezoneType === "event"
	// 			) {
	// 				endDate = endDate.setZone(this.event.timezone);
	// 			}
	// 			this.selectedEndDate = { ...day, isoDate: endDate.endOf("day").toISO() };
	// 		} else {
	// 			let startDate = DateTime.fromISO(day.isoDate).setLocale(
	// 				this.STranslate.currentLang
	// 					? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
	// 							"-" +
	// 							this.STranslate.currentLang.slice(2, 4).toUpperCase()
	// 					: this.event.language.slice(0, 2).toLowerCase() +
	// 							"-" +
	// 							this.event.language.slice(2, 4).toUpperCase()
	// 			);

	// 			if (
	// 				this.event &&
	// 				this.event.timezoneType === "local" &&
	// 				this.eventUser &&
	// 				this.eventUser.updatedSettings &&
	// 				this.eventUser.updatedSettings.timezoneType === "event"
	// 			) {
	// 				startDate = startDate.setZone(this.event.timezone);
	// 			}
	// 			this.selectedStartDate = { ...day, isoDate: startDate.startOf("day").toISO() };
	// 			this.selectedEndDate = { ...day, isoDate: startDate.endOf("day").toISO() };
	// 		}

	// 		this.dateSelectedEvent.emit({
	// 			startDate: this.selectedStartDate ? this.selectedStartDate.isoDate : null,
	// 			endDate: this.selectedEndDate ? this.selectedEndDate.isoDate : null
	// 		});
	// 	}
	// }

	// timeOfSelectedDatesUpdated() {
	// 	this.dateSelectedEvent.emit({
	// 		startDate: this.selectedStartDate ? this.selectedStartDate.isoDate : null,
	// 		endDate: this.selectedEndDate ? this.selectedEndDate.isoDate : null
	// 	});
	// }

	// checkYearOnDates(year: number) {
	// 	return this.dates.find((date) => date.yearNumber === year);
	// }

	// checkMonthOnYear(year: IYear, monthNumber: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12) {
	// 	return year.months[monthNumber] &&
	// 		year.months[monthNumber].monthNumber === monthNumber &&
	// 		year.months[monthNumber].weeks &&
	// 		year.months[monthNumber].weeks.length > 0
	// 		? year.months[monthNumber]
	// 		: null;
	// }

	// checkWeekOfMonth(month: IMonth, weekNumber: number) {
	// 	return month.weeks.find((week) => week.weekNumber === weekNumber);
	// }

	// checkDayOfWeek(week: IWeek, dayNumber: number) {
	// 	return week.days.find((day) => day.dayNumber === dayNumber);
	// }

	// checkSelectedDate(day: IDay) {
	// 	const dateTimeDay = DateTime.fromISO(day.isoDate);
	// 	const dateTimeSelectedStart = this.selectedStartDate ? DateTime.fromISO(this.selectedStartDate.isoDate) : null;
	// 	const dateTimeSelectedEnd = this.selectedEndDate ? DateTime.fromISO(this.selectedEndDate.isoDate) : null;
	// 	if (
	// 		this.selectedStartDate &&
	// 		this.selectedStartDate.dayNumber === day.dayNumber &&
	// 		this.selectedStartDate.monthNumber === day.monthNumber &&
	// 		this.selectedStartDate.yearNumber === day.yearNumber
	// 	) {
	// 		return true;
	// 	} else if (
	// 		this.selectedEndDate &&
	// 		this.selectedEndDate.dayNumber === day.dayNumber &&
	// 		this.selectedEndDate.monthNumber === day.monthNumber &&
	// 		this.selectedEndDate.yearNumber === day.yearNumber
	// 	) {
	// 		return true;
	// 	} else if (
	// 		this.selectedStartDate &&
	// 		this.selectedEndDate &&
	// 		dateTimeSelectedStart &&
	// 		dateTimeSelectedEnd &&
	// 		dateTimeDay <= dateTimeSelectedEnd &&
	// 		dateTimeDay >= dateTimeSelectedStart
	// 	) {
	// 		return true;
	// 	} else {
	// 		return false;
	// 	}
	// }

	// checkCurrentDate(day: IDay) {
	// 	const dateTimeDay = DateTime.fromISO(day.isoDate);
	// 	return this.savedCurrentDateTime &&
	// 		dateTimeDay &&
	// 		this.savedCurrentDateTime.day === dateTimeDay.day &&
	// 		this.savedCurrentDateTime.month === dateTimeDay.month &&
	// 		this.savedCurrentDateTime.year === dateTimeDay.year
	// 		? true
	// 		: false;
	// }

	// goNext() {
	// 	if (this.viewType === "month") {
	// 		this.currentDateTime.set(this.currentDateTime().plus({ month: 1 }));
	// 		this.generateDatesOfCurrentDate();
	// 	} else {
	// 		this.currentWeekDateTime.set(this.currentWeekDateTime().plus({ week: 1 }));
	// 		if (
	// 			this.currentDateTime().month !== this.currentWeekDateTime().month ||
	// 			this.currentDateTime().year !== this.currentWeekDateTime().year
	// 		) {
	// 			this.currentDateTime.set(this.currentDateTime().plus({ month: 1 }));
	// 		}
	// 		this.generateDatesOfCurrentDate();
	// 	}
	// }

	// goPrev() {
	// 	if (this.viewType === "month") {
	// 		this.currentDateTime.set(this.currentDateTime().minus({ month: 1 }));
	// 		this.generateDatesOfCurrentDate();
	// 	} else {
	// 		this.currentWeekDateTime.set(this.currentWeekDateTime().minus({ week: 1 }));
	// 		if (
	// 			this.currentDateTime().month !== this.currentWeekDateTime().month ||
	// 			this.currentDateTime().year !== this.currentWeekDateTime().year
	// 		) {
	// 			this.currentDateTime.set(this.currentDateTime().minus({ month: 1 }));
	// 		}
	// 		this.generateDatesOfCurrentDate();
	// 	}
	// }

	// getDateOfDatasForBadge(date: string) {
	// 	return this.datasForBadges.find((data) => {
	// 		let dateDateTime = DateTime.fromISO(data.date).setLocale(
	// 			this.STranslate.currentLang
	// 				? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.STranslate.currentLang.slice(2, 4).toUpperCase()
	// 				: this.event.language.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.event.language.slice(2, 4).toUpperCase()
	// 		);

	// 		if (
	// 			this.event &&
	// 			this.event.timezoneType === "local" &&
	// 			this.eventUser &&
	// 			this.eventUser.updatedSettings &&
	// 			this.eventUser.updatedSettings.timezoneType === "event"
	// 		) {
	// 			dateDateTime = dateDateTime.setZone(this.event.timezone);
	// 		}

	// 		let dayDateTime = DateTime.fromISO(date).setLocale(
	// 			this.STranslate.currentLang
	// 				? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.STranslate.currentLang.slice(2, 4).toUpperCase()
	// 				: this.event.language.slice(0, 2).toLowerCase() +
	// 						"-" +
	// 						this.event.language.slice(2, 4).toUpperCase()
	// 		);

	// 		if (
	// 			this.event &&
	// 			this.event.timezoneType === "local" &&
	// 			this.eventUser &&
	// 			this.eventUser.updatedSettings &&
	// 			this.eventUser.updatedSettings.timezoneType === "event"
	// 		) {
	// 			dayDateTime = dayDateTime.setZone(this.event.timezone);
	// 		}

	// 		if (
	// 			dayDateTime.day === dateDateTime.day &&
	// 			dayDateTime.month === dateDateTime.month &&
	// 			dayDateTime.year === dateDateTime.year
	// 		) {
	// 			return true;
	// 		} else {
	// 			return false;
	// 		}
	// 	});
	// }

	// checkNumberOfDatasForDate(day: IDay) {
	// 	const date = this.getDateOfDatasForBadge(day.isoDate);

	// 	if (date && date.totalDatas > 0) {
	// 		return date.totalDatas;
	// 	} else {
	// 		return 0;
	// 	}
	// }
}
